import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { ClockCircleOutlined, EditOutlined } from '@ant-design/icons';
import { queries } from '../../../graphql/graphql';
import { SiteVisitView } from '../../../modules/SiteVisit';
import { ProposalView } from '../../../components/Proposals/View';

const { Text } = Typography;

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getTypeLabel = (type?: string) => {
  if (type === 'SITE_PLAN') return 'Site plan';
  if (type === 'PROPOSAL') return 'Proposal';
  return 'Order';
};

const getStatusLabel = (status?: string) => {
  if (status === 'DONE') return 'Done';
  if (status === 'WAITING') return 'Waiting';
  return 'Unknown';
};

const getStatusColor = (status?: string) => {
  if (status === 'DONE') return 'green';
  if (status === 'WAITING') return 'gold';
  return 'default';
};

const extractSiteVisitId = (data?: Record<string, unknown>) => {
  if (!data) return undefined;
  const raw = data.siteVisitId;
  if (typeof raw === 'string') return raw;
  if (
    raw &&
    typeof raw === 'object' &&
    typeof (raw as { toString?: () => string }).toString === 'function'
  ) {
    const value = (raw as { toString: () => string }).toString();
    return value === '[object Object]' ? undefined : value;
  }
  return undefined;
};

const OrderProposalsTabs: React.FC<{ orderId: string }> = ({ orderId }) => {
  const { data, loading } = useQuery(queries.proposal.ORDER_PROPOSALS, {
    variables: { orderId },
    skip: !orderId,
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return (
      <div style={{ padding: '32px 0', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const proposals = data?.orderProposals || [];
  if (!proposals.length) {
    return <Empty description="No proposals yet" />;
  }

  const respondedProposals = proposals.filter(
    (proposal: { status?: string }) => proposal.status === 'RESPONSES_RECEIVED'
  );
  const pendingProposals = proposals.filter(
    (proposal: { status?: string }) => proposal.status !== 'RESPONSES_RECEIVED'
  );

  return (
    <Tabs
      type="card"
      items={[
        ...respondedProposals.map((proposal: { id: string }, index: number) => ({
          key: proposal.id,
          label: `GC ${index + 1}`,
          children: <ProposalView id={proposal.id} />,
        })),
        ...pendingProposals.map((proposal: { id: string }, index: number) => ({
          key: `${proposal.id}-pending`,
          label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ClockCircleOutlined style={{ fontSize: 12, color: '#4b4b4b' }} />
              {`GC ${respondedProposals.length + index + 1}`}
            </span>
          ),
          children: (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <ClockCircleOutlined style={{ fontSize: 48, color: '#111' }} />
              <Typography.Title level={5} style={{ marginTop: 16 }}>
                Proposal in progress
              </Typography.Title>
              <Typography.Paragraph style={{ margin: 0, color: '#4b4b4b' }}>
                We have sent your request to the builder. We will reach out shortly to confirm the
                next step.
              </Typography.Paragraph>
            </div>
          ),
        })),
      ]}
    />
  );
};

const OrderView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading } = useQuery(queries.order.ORDER, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const order = data?.order;

  if (!loading && !order) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col>
          <Card>
            <h2>Order not found</h2>
            <Button type="primary" onClick={() => navigate('/admin/orders')}>
              Back to Orders
            </Button>
          </Card>
        </Col>
      </Row>
    );
  }

  if (loading && !order) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col>
          <Spin size="large" />
        </Col>
      </Row>
    );
  }

  const createdByLabel =
    order?.createdBy?.name || order?.createdBy?.email || order?.createdById || 'Unknown user';
  const orderData = (order?.data || {}) as Record<string, unknown>;
  const siteVisitId = extractSiteVisitId(orderData);

  return (
    <Row gutter={[16, 16]} justify="center">
      <Col span={24}>
        <Card
          extra={
            <Space>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`/admin/order/edit/${order.id}`)}>
                Edit
              </Button>
              <Button onClick={() => navigate('/admin/orders')}>Back to list</Button>
            </Space>
          }>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Order ID">{order.id}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color="blue">{getTypeLabel(order.type)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(order.status)}>{getStatusLabel(order.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Title">{order.title}</Descriptions.Item>
            <Descriptions.Item label="Created by">{createdByLabel}</Descriptions.Item>
            <Descriptions.Item label="Created at">{formatDate(order.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Updated at">{formatDate(order.updatedAt)}</Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col span={24}>
        {order.type === 'SITE_PLAN' ? (
          siteVisitId ? (
            <SiteVisitView
              id={siteVisitId}
              onEdit={() => navigate(`/site-visit/edit/${siteVisitId}`)}
              showShare={false}
            />
          ) : (
            <Empty description="Site visit is not linked to this order yet." />
          )
        ) : order.type === 'PROPOSAL' ? (
          <Card>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Text type="secondary">Contractor proposals</Text>
              <OrderProposalsTabs orderId={order.id} />
            </Space>
          </Card>
        ) : (
          <Empty description="Unsupported order type." />
        )}
      </Col>
    </Row>
  );
};

export default OrderView;
